import { Button, Flex, TextField } from '@brightdrop/bd-ui';
import { Checkbox, FormControlLabel, Link, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useValidateCredentialsMutation } from '~/api/generated-api';
import CheckIcon from '~/assets/icons/new/greenCircledCheckMark.svg?react';
import { useOrgId } from '~/common/hooks/useOrgId';
import PartnerLogo from '~/features/integrations/partners/PartnerLogo';
import type { WizardStepProps } from '~/features/integrations/partners/types';
import WizardSection from '~/features/integrations/wizard/WizardSection';

import { PARTNER_IDS } from '../types';
import useStyles from './CoastConnectStep.styles';

const COAST_API_KEY_URL = 'https://portal.coastpay.com/api-keys/';
const COAST_CREATE_ACCOUNT_URL = 'https://coastpay.com/';
// Coast has not provided a Data Sharing Terms PDF URL yet.
const COAST_DATA_SHARING_TERMS_URL = '';
// Identifies which terms version the checkbox in section 3 accepts.
const COAST_TERMS_NAME = 'agreement-1';
const COAST_TERMS_VERSION = 'fuel-card-coast-2026-01';

type VerifyStatus = 'idle' | 'verifying' | 'verified' | 'error';

/** Coast's first wizard step: connect account, verify API key, accept terms. */
const CoastConnectStep = ({
  draft,
  onChange,
  onValidityChange,
  setAlertBanners,
  termsRequired = true,
}: WizardStepProps) => {
  const { t } = useTranslation(['integrations', 'common']);
  const { classes } = useStyles();
  const organizationsId = useOrgId();
  const [validateCredentials] = useValidateCredentialsMutation();

  const [apiKeyInput, setApiKeyInput] = useState(draft.apiKey ?? '');
  const [verifyStatus, setVerifyStatus] = useState<VerifyStatus>(
    draft.apiKey ? 'verified' : 'idle'
  );

  const hasAcceptedTerms = !!draft.hasAcceptedTerms;

  useEffect(() => {
    onValidityChange(
      verifyStatus === 'verified' && (!termsRequired || hasAcceptedTerms)
    );
  }, [verifyStatus, hasAcceptedTerms, termsRequired, onValidityChange]);

  const handleApiKeyChange = (value: string) => {
    setApiKeyInput(value);
    if (verifyStatus !== 'idle') {
      setVerifyStatus('idle');
      setAlertBanners([]);
    }
  };

  const handleVerify = async () => {
    setVerifyStatus('verifying');
    try {
      const response = await validateCredentials({
        'cs-organization': organizationsId,
        validateCredentialsRequestDto: {
          provider: { providerCode: 'COAST', authType: 'API_KEY' },
          credentials: { apiKey: apiKeyInput },
          authType: 'API_KEY',
        },
      }).unwrap();

      if (response.result?.valid) {
        setVerifyStatus('verified');
        onChange({ apiKey: apiKeyInput });
        return;
      }
    } catch {
      // Falls through to the shared error banner below.
    }

    setVerifyStatus('error');
    setAlertBanners([
      {
        variant: 'error',
        title: t('integrations:coast.connectStep.apiKeyVerifyError'),
        testId: 'coast-connect-step-verify-error-banner',
        open: true,
        showCloseIcon: true,
        onClose: () => setAlertBanners([]),
      },
    ]);
  };

  const handleTermsChange = (checked: boolean) => {
    onChange({
      hasAcceptedTerms: checked,
      hasAgreedToShareData: checked,
      consents: checked
        ? [
            {
              termsName: COAST_TERMS_NAME,
              termsVersion: COAST_TERMS_VERSION,
              acceptedAt: new Date().toISOString(),
            },
          ]
        : [],
    });
  };

  return (
    <>
      <WizardSection
        number={1}
        title={t('integrations:coast.connectStep.sectionOneTitle')}
        trailing={<PartnerLogo partnerId={PARTNER_IDS.COAST} variant="icon" />}
        testId="coast-connect-step-section-1"
      >
        <Typography variant="small_body_regular" className={classes.helperText}>
          {t('integrations:coast.connectStep.sectionOneBody')}
        </Typography>
        <Button
          variant="primary"
          href={COAST_API_KEY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="coast-connect-step-get-api-key"
        >
          {t('integrations:coast.connectStep.getApiKey')}
        </Button>
        <Typography variant="small_body_regular" className={classes.helperText}>
          <Trans
            t={t}
            i18nKey="integrations:coast.connectStep.createAccountPrompt"
            components={{
              createAccountLink: (
                <Link
                  href={COAST_CREATE_ACCOUNT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="coast-connect-step-create-account-link"
                />
              ),
            }}
          />
        </Typography>
      </WizardSection>

      <WizardSection
        number={2}
        title={t('integrations:coast.connectStep.sectionTwoTitle')}
        testId="coast-connect-step-section-2"
      >
        <Flex className={classes.apiKeyRow}>
          <TextField
            name="coast-api-key"
            label={t('integrations:coast.connectStep.apiKeyFieldLabel')}
            value={apiKeyInput}
            onChange={(e) => handleApiKeyChange(e.target.value)}
            disabled={verifyStatus === 'verifying'}
            className={classes.apiKeyField}
            autoComplete="off"
            redactFromHeap
          />
          {verifyStatus === 'verified' ? (
            <div className={classes.verifiedRow}>
              <CheckIcon className={classes.checkIcon} />
              <Typography
                variant="small_body_medium"
                className={classes.verifiedText}
                data-testid="coast-connect-step-verified-label"
              >
                {t('integrations:coast.connectStep.apiKeyVerified')}
              </Typography>
            </div>
          ) : (
            <Button
              variant="secondary"
              disabled={!apiKeyInput.trim() || verifyStatus === 'verifying'}
              isLoading={verifyStatus === 'verifying'}
              onClick={handleVerify}
              data-testid="coast-connect-step-verify-button"
            >
              {t('integrations:coast.connectStep.verify')}
            </Button>
          )}
        </Flex>
      </WizardSection>

      <WizardSection
        number={3}
        title={t('integrations:coast.connectStep.sectionThreeTitle')}
        testId="coast-connect-step-section-3"
      >
        <Typography variant="small_body_regular" className={classes.helperText}>
          {t('integrations:coast.connectStep.termsBody')}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={hasAcceptedTerms}
              onChange={(e) => handleTermsChange(e.target.checked)}
              className={classes.checkbox}
              data-testid="coast-connect-step-terms-checkbox"
            />
          }
          label={
            <Trans
              t={t}
              i18nKey="integrations:coast.connectStep.termsCheckboxLabel"
              components={{
                termsLink: (
                  <Link
                    href={COAST_DATA_SHARING_TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.termsLink}
                    data-testid="coast-connect-step-terms-link"
                  />
                ),
              }}
            />
          }
        />
      </WizardSection>
    </>
  );
};

export default CoastConnectStep;
